import React, { useCallback, useMemo, useRef } from 'react'
import { ChartType, chartTypeFromJSON } from '../../proto/generated/chart'
import Bar from '../../assets/icons/chartTypes/bars'
import Candle from '../../assets/icons/chartTypes/candles'
import HollowCandle from '../../assets/icons/chartTypes/hollowCandles'
import HighLowCandle from '../../assets/icons/chartTypes/highLowCandles'
import Area from '../../assets/icons/chartTypes/area'
import Histogram from '../../assets/icons/chartTypes/histogram'
import Column from '../../assets/icons/plotTypes/columns'
import Line from '../../assets/icons/chartTypes/line'
import Baseline from '../../assets/icons/chartTypes/baseline'
import { SuperChartConfiguration_ChartType } from '../../proto/generated/superchart-page'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Divider, ListItem, ListItemButton, ListItemIcon, ListItemText, Tooltip, useTheme } from '@mui/material'
import { ShortcutTooltipTitle, ShortcutKeys } from '../KeyboardShortcuts/ShortcutTooltipTitle'
import { AppMenu } from '../common/AppMenu'
import { updateChartType, updateScreenerChartType } from '../../reducer/chart.reducer'
import { selectChartType, selectChartTypes, selectScreenerChartType } from '../../selectors/chart'
import { ExtraSmallButton, UltraSmallButton } from '../custom'
import HeikinAshi from '../../assets/icons/chartTypes/heikinAshi'
import LineBreak from '../../assets/icons/chartTypes/LineBreak'
import Kagi from '../../assets/icons/chartTypes/Kagi'
import { ShortcutsCategories, ShortcutType } from '../KeyboardShortcuts/Shortcuts.types'
import { ChartTypeShortcutsKeyName } from '../KeyboardShortcuts/ShortcutsKeymap'
import useShortcuts from '../../hooks/useShortcuts'
import Renko from '../../assets/icons/chartTypes/Renko'
import PandF from '../../assets/icons/chartTypes/PandF'
import { selectMultiPaneFullScreen } from '../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../assets/constants'
import { ActionType, useUndoRedoContext } from '../UndoRedoFunctonalities/UndoRedo.context'
import AnalyticsManager from '../../analytics/AnalyticsManager'
import HlcArea from '../../assets/icons/chartTypes/hlcArea'
import VolumeCandles from '../../assets/icons/chartTypes/volumeCandles'
import { FeatureCategory, FeatureSubCategory } from '../adminUserRestrictions/Restrictions.constants'
import { Group, setFeatureRestrictionDialog } from '../../reducer/user.reducer'
import { useUser } from '../../context/UserContext'
import LineMarkers from '../../assets/icons/chartTypes/lineMarkers.tsx'
import StepLine from '../../assets/icons/plotTypes/stepLine.tsx'
import { selectFavoriteChartTypes } from '../../selectors/user.ts'
import { updateUserPreferences } from '../../services/apis/user.ts'
import StarOutlinedIcon from '../../assets/icons/StarOutlinedIcon.tsx'
import { Star } from '@mui/icons-material'
import ThinArrowDownIcon from '../../assets/icons/ThinArrowDownIcon'
import ThinArrowUpIcon from '../../assets/icons/ThinArrowUpIcon'

const menuActionIconSx = { fontSize: 18, width: 18, height: 18 } as const

const CHART_TYPE_MENU_ICON_SIZE = 24

const chartTypeMenuListSx = { py: 0.5 } as const

const chartTypeMenuItemButtonSx = {
    py: 0.75,
    px: 1,
    minHeight: 36,
    gap: 1
} as const

const chartTypeMenuItemIconSx = {
    minWidth: CHART_TYPE_MENU_ICON_SIZE,
    width: CHART_TYPE_MENU_ICON_SIZE,
    m: 0,
    p: 0,
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    '& svg': {
        width: CHART_TYPE_MENU_ICON_SIZE,
        height: CHART_TYPE_MENU_ICON_SIZE
    },
    '& .MuiSvgIcon-root': {
        fontSize: CHART_TYPE_MENU_ICON_SIZE
    }
} as const

const chartTypeMenuItemTextSx = { m: 0 } as const


interface ChartTypeMenuProps {
    viewKey: string
    disabled?: boolean
    screenerChart?: boolean
}

/** Backend chart type config may still use HISTOGRAM code for the Columns chart type. */
export function resolveChartMenuType(chartType: SuperChartConfiguration_ChartType): ChartType {
    if (chartType.name === 'Columns') {
        return ChartType.COLUMN
    }
    return chartTypeFromJSON(chartType.code)
}

export function getIcon(type: ChartType) {
    switch (type) {
        case ChartType.BAR:
            return <Bar />
        case ChartType.CANDLESTICK:
            return <Candle />
        case ChartType.AREA:
            return <Area />
        case ChartType.LINE:
            return <Line />
        case ChartType.HISTOGRAM:
            return <Histogram />
        case ChartType.COLUMN:
            return <Column />
        case ChartType.HEIKIN_ASHI:
            return <HeikinAshi />
        case ChartType.LINE_BREAK:
            return <LineBreak />
        case ChartType.KAGI:
            return <Kagi />
        case ChartType.RENKO:
            return <Renko />
        case ChartType.POINT_AND_FIGURE:
            return <PandF />
        case ChartType.HOLLOW_CANDLES:
            return <HollowCandle />;
        case ChartType.HIGH_LOW:
            return <HighLowCandle />;
        case ChartType.BASELINE:
            return <Baseline />;
        case ChartType.HLC_AREA:
            return <HlcArea />;
        case ChartType.VOLUME_CANDLESTICK:
            return <VolumeCandles />;
        case ChartType.STEP_LINE_CHART:
            return <StepLine />;
        case ChartType.LINE_WITH_MARKERS:
            return <LineMarkers />;
        default:
            return null
    }
}

const ChartTypeMenu = React.memo(({ viewKey, disabled = false, screenerChart = false }: ChartTypeMenuProps) => {
    const selectedChartType = useSelector(screenerChart ? selectScreenerChartType : selectChartType(viewKey))
    const displayChartType = useMemo(() => {
        if (selectedChartType == null || selectedChartType === ChartType.CHART_TYPE_UNKNOWN) {
            return ChartType.CANDLESTICK
        }
        return selectedChartType
    }, [selectedChartType])
    const { push } = useUndoRedoContext()
    const allChartTypes = useSelector(selectChartTypes)
    
    
    const chartTypes = useMemo(() => {
        if (screenerChart) {
            const allowedTypes = [ChartType.BASELINE, ChartType.BAR, ChartType.LINE, ChartType.CANDLESTICK]
            return allChartTypes.filter(ct => allowedTypes.includes(chartTypeFromJSON(ct.code)))
        }
        return allChartTypes
    }, [screenerChart, allChartTypes])
    
    const dispatch = useDispatch()
    const [anchorPosition, setAnchorPosition] = React.useState({ top: 0, left: 0 })
    const open = anchorPosition.top !== 0 || anchorPosition.left !== 0
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const setMenuAnchorFromElement = useCallback((element: HTMLElement | null) => {
        if (!element) return
        const rect = element.getBoundingClientRect()
        setAnchorPosition({ top: rect.bottom, left: rect.left })
    }, [])
    const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
        if (disabled) return
        setMenuAnchorFromElement(event.currentTarget)
    }
    const { user: { hasAccess } } = useUser()

    const handleClose = useCallback(() => {
        setAnchorPosition({ top: 0, left: 0 })
    }, [])

    const theme = useTheme()

    const shortcuts: ShortcutType = useMemo(() => ({
        [ChartTypeShortcutsKeyName.openChartTypeMenu]: () => {
            if (disabled) return
            AnalyticsManager.logEvent('Chart Type Menu Opened', { source: 'keyboard' })
            setMenuAnchorFromElement(buttonRef?.current)
        }
    }), [buttonRef, disabled, setMenuAnchorFromElement])

    useShortcuts(ShortcutsCategories.CHART_TYPE, shortcuts)
    const container = useSelector(selectMultiPaneFullScreen)

    const favoriteChartTypes = useSelector(selectFavoriteChartTypes)

    const toggleFavorite = useCallback((chartType: SuperChartConfiguration_ChartType) => {
        const typeCode = chartType.code
        const newFavorites = [...favoriteChartTypes]
        if (newFavorites.includes(typeCode)) {
            newFavorites.splice(newFavorites.indexOf(typeCode), 1)
        } else {
            newFavorites.push(typeCode)
        }
        updateUserPreferences({
            body: {
                favoriteChartTypes: newFavorites
            }
        })
    }, [favoriteChartTypes])

    const onChartTypeMenuItemClicked = useCallback((type: ChartType, chartTypeName?: string) => {
        if (selectedChartType !== type) {
            if (screenerChart) {
                dispatch(updateScreenerChartType(type))
            } else {
                dispatch(updateChartType({ viewKey, chartType: type }))
                push(
                    [{
                        chartID: viewKey,
                        currentData: type,
                        previousData: selectedChartType
                    }],
                    `Chart Type (${chartTypeName ?? type})`,
                    ActionType.CHART_TYPE
                )
            }
        }
        handleClose()
    }, [selectedChartType, viewKey, screenerChart, dispatch, push, handleClose])

    const handleFavoriteClick = useCallback((type: ChartType) => {
        if (selectedChartType !== type) {
            onChartTypeMenuItemClicked(type)
        }
    }, [selectedChartType, onChartTypeMenuItemClicked])

    const typesDisplayList = useMemo(() => {
        if (favoriteChartTypes.length === 0) return []

        const list = [...favoriteChartTypes]
        // Check if selected is in favorites
        const isSelectedFav = list.some(code => {
            const config = chartTypes.find(c => c.code === code)
            return config ? resolveChartMenuType(config) === selectedChartType : chartTypeFromJSON(code) === selectedChartType
        })

        if (!isSelectedFav) {
            const selectedConfig = chartTypes.find(c => resolveChartMenuType(c) === selectedChartType)
            if (selectedConfig) {
                list.push(selectedConfig.code)
            }
        }
        return list
    }, [favoriteChartTypes, selectedChartType, chartTypes])

    const shortcutTooltipTitle = (
        <ShortcutTooltipTitle label="Chart type">
            <ShortcutKeys keys={['⌥', 'T']} />
        </ShortcutTooltipTitle>
    )

    const chartTypeMenuSegments = useMemo(() => {
        const groups = chartTypes.reduce((acc, chartType) => {
            const group = chartType.group || 'OTHER';
            if (!acc[group]) acc[group] = [];
            acc[group].push(chartType);
            return acc;
        }, {} as Record<string, SuperChartConfiguration_ChartType[]>);

        const barChartType = groups.BAR?.find((ct) => chartTypeFromJSON(ct.code) === ChartType.BAR);
        if (!screenerChart && barChartType) {
            const candleTypes = [...(groups.CANDLE ?? [])];
            const volumeIndex = candleTypes.findIndex(
                (ct) => chartTypeFromJSON(ct.code) === ChartType.VOLUME_CANDLESTICK
            );
            const insertIndex = volumeIndex >= 0 ? volumeIndex + 1 : candleTypes.length;
            candleTypes.splice(insertIndex, 0, barChartType);
            groups.CANDLE = candleTypes;

            const remainingBarTypes = (groups.BAR ?? []).filter(
                (ct) => chartTypeFromJSON(ct.code) !== ChartType.BAR
            );
            if (remainingBarTypes.length) {
                groups.BAR = remainingBarTypes;
            } else {
                delete groups.BAR;
            }
        }

        const groupOrder = ['CANDLE', 'LINE', 'AREA', 'BAR', 'PRICE_BASED', 'OTHER'];
        const segments: { key: string; types: SuperChartConfiguration_ChartType[] }[] = [];

        for (const group of groupOrder) {
            if (groups[group]?.length) {
                segments.push({ key: group, types: groups[group] });
            }
        }
        Object.keys(groups).forEach((group) => {
            if (!groupOrder.includes(group) && groups[group]?.length) {
                segments.push({ key: group, types: groups[group] });
            }
        });

        return segments;
    }, [chartTypes, screenerChart]);

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {favoriteChartTypes.length > 0 ? (
                <>
                    {typesDisplayList.map((typeCode) => {
                        const chartTypeConfig = chartTypes.find(c => c.code === typeCode)
                        const type = chartTypeConfig ? resolveChartMenuType(chartTypeConfig) : chartTypeFromJSON(typeCode)
                        return (
                            <Tooltip key={typeCode} title={chartTypeConfig?.name ?? ''} arrow placement="bottom">
                                <span>
                                    <ExtraSmallButton
                                        size="small"
                                        color="inherit"
                                        disabled={disabled}
                                        onClick={() => onChartTypeMenuItemClicked(type)}
                                        sx={{
                                            backgroundColor: selectedChartType === type ? (theme) => theme.palette.action.selected : 'transparent',
                                            margin: '0 2px'
                                        }}
                                    >
                                        {getIcon(type)}
                                    </ExtraSmallButton>
                                </span>
                            </Tooltip>
                        )
                    })}
                    <Tooltip
                        title={shortcutTooltipTitle}
                        arrow
                        placement="bottom"
                    >
                        <span>
                        <ExtraSmallButton ref={buttonRef} size="small" color="inherit" onClick={handleButtonClick}
                            id={viewKey === 'screener-chart-' ? undefined : 'chart-type-menu-button'}
                            disabled={disabled}
                            data-event-source={viewKey === 'screener-chart-' ? 'stock-screener' : 'stock-bar'}
                            data-event={'ChartType Menu Opened'}>
                            {open ? <ThinArrowUpIcon fontSize={'small'} /> : <ThinArrowDownIcon fontSize={'small'} />}
                        </ExtraSmallButton>
                        </span>
                    </Tooltip>
                </>
            ) : (
                <Tooltip
                    title={shortcutTooltipTitle}
                    arrow
                    placement="bottom"
                >
                    <span>
                    <ExtraSmallButton ref={buttonRef} size="small" color="inherit" onClick={handleButtonClick}
                        id={viewKey === 'screener-chart-' ? undefined : 'chart-type-menu-button'}
                        disabled={disabled}
                        data-event-source={viewKey === 'screener-chart-' ? 'stock-screener' : 'stock-bar'}
                        data-event={'ChartType Menu Opened'}>
                        {getIcon(displayChartType)}
                    </ExtraSmallButton>
                    </span>
                </Tooltip>
            )}
            <AppMenu
                anchorReference="anchorPosition"
                anchorPosition={anchorPosition}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                }}
                open={open}
                onClose={handleClose}
                clickAwayIgnoreRefs={[buttonRef]}
                container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                transitionDuration={0}
                MenuListProps={{ dense: true, sx: chartTypeMenuListSx }}
            >
                {chartTypeMenuSegments.map(({ key, types }, index) => (
                    <div key={key}>
                        {index > 0 && !screenerChart && <Divider
                            sx={{
                                my: 0.5,
                                backgroundColor: theme.palette.mode === 'dark' ? '#434651' : '#e0e3eb'
                            }}
                        />}
                        {types.map((chartType) => {
                            const type = resolveChartMenuType(chartType);
                            const isFavorite = favoriteChartTypes.includes(chartType.code)
                            return (
                                <ListItem
                                    key={chartType.code}
                                    disablePadding
                                    sx={{
                                        '&:hover .star-icon-button': {
                                            visibility: 'visible'
                                        },
                                    }}
                                    secondaryAction={
                                        <UltraSmallButton
                                            color="inherit"
                                            className="star-icon-button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                toggleFavorite(chartType)
                                            }}
                                            sx={{
                                                visibility: isFavorite ? 'visible' : 'hidden',
                                            }}
                                        >
                                            {isFavorite ? <Star sx={menuActionIconSx} /> : <StarOutlinedIcon sx={menuActionIconSx} />}
                                        </UltraSmallButton>
                                    }
                                >
                                    <ListItemButton
                                        selected={selectedChartType === type}
                                        onClick={() => onChartTypeMenuItemClicked(type, chartType.name)}
                                        data-event={`Chart Candle Type Changed`}
                                        data-event-candle-type={chartType.name}
                                        data-event-source={viewKey === 'screener-chart-' ? 'stock-screener' : 'stock-bar'}
                                        sx={chartTypeMenuItemButtonSx}
                                    >
                                        <ListItemIcon sx={chartTypeMenuItemIconSx}>
                                            {getIcon(type)}
                                        </ListItemIcon>
                                        <ListItemText sx={chartTypeMenuItemTextSx}>{chartType.name}</ListItemText>
                                    </ListItemButton>
                                </ListItem>
                            );
                        })}
                    </div>
                ))}
            </AppMenu>
        </div>
    )
})

export default ChartTypeMenu
